// Pure transforms from the `POST /v1/metrics:query` degenerate envelope
// (`series[] -> groups[] -> points[]`, docs/04 §11) into the closed-set Chart
// primitive shapes. The Dashboard screen composes these; keeping them pure
// and free of React/network lets the screen's
// presentational `DashboardView` stay fixture-driven (the repo's story-first
// screen pattern) and lets every widget pick a geometry the primitive supports.
//
// The metrics contract exposes no `compare_to` param, so period deltas are
// computed client-side by querying the immediately-preceding equal-length
// window (`previousPeriod`) — two reads, no invented backend field.

import type { components } from "../api/schema";
import { shortResourceId } from "./shellNavigation";
import type {
  ChartDatum,
  ChartSegment,
  ChartStackedDatum,
} from "../ui/chartTypes";

export type MetricsQueryResponse =
  components["schemas"]["MetricsQueryResponse"];
export type MetricName = components["schemas"]["MetricName"];
export type MetricBucket = components["schemas"]["MetricBucket"];
export type MetricDimension = components["schemas"]["MetricDimension"];
type MetricSeries = components["schemas"]["MetricSeries"];
type MetricGroup = components["schemas"]["MetricGroup"];
type MetricPoint = components["schemas"]["MetricPoint"];
export type MarkerDisplayNames = ReadonlyMap<string, string>;

// --- time-range presets ------------------------------------------------

export type RangePreset = "24h" | "7d" | "30d" | "90d";

export const RANGE_PRESET_LABELS: Record<RangePreset, string> = {
  "24h": "Last 24 hours",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
};

const PRESET_DAYS: Record<RangePreset, number> = {
  "24h": 1,
  "7d": 7,
  "30d": 30,
  "90d": 90,
};

const DAY_MS = 24 * 60 * 60 * 1000;

export interface TimeWindow {
  start: string;
  end: string;
}

export function rangeForPreset(
  preset: RangePreset,
  now: Date = new Date(),
): TimeWindow {
  const end = now;
  const start = new Date(now.getTime() - PRESET_DAYS[preset] * DAY_MS);
  return { start: start.toISOString(), end: end.toISOString() };
}

// Fewer, wider buckets for the long window so an over-time line stays
// legible; mirrors the server's auto-bucket derivation for `:render`.
export function bucketForPreset(preset: RangePreset): MetricBucket {
  if (preset === "24h") {
    return "hour";
  }
  return preset === "90d" ? "week" : "day";
}

// The equal-length window immediately before `[start, end)` — the client-side
// stand-in for a `compare_to=previous_period` the contract does not expose.
export function previousPeriod(window: TimeWindow): TimeWindow {
  const start = new Date(window.start).getTime();
  const end = new Date(window.end).getTime();
  const span = end - start;
  return {
    start: new Date(start - span).toISOString(),
    end: new Date(start).toISOString(),
  };
}

// --- labels + formatting ----------------------------------------------

const UUID_DIMENSIONS = new Set<MetricDimension>([
  "agent_id",
  "agent_version_id",
  "marker_id",
  "persona_id",
  "scenario_id",
  "scenario_set_id",
  "batch_id",
]);

function titleCase(value: string): string {
  return value
    .split("_")
    .map((word, index) =>
      index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word,
    )
    .join(" ");
}

export function dimensionValueLabel(
  dimension: MetricDimension,
  value: string,
  markerDisplayNames?: MarkerDisplayNames,
): string {
  if (dimension === "marker_id") {
    return markerDisplayNames?.get(value) ?? shortResourceId(value);
  }
  if (UUID_DIMENSIONS.has(dimension)) {
    return shortResourceId(value);
  }
  return titleCase(value);
}

function groupLabel(
  group: MetricGroup,
  markerDisplayNames?: MarkerDisplayNames,
): string {
  const dimensions = group.dimensions ?? [];
  if (dimensions.length === 0) {
    return "All";
  }
  return dimensions
    .map((coordinate) =>
      dimensionValueLabel(
        coordinate.dimension,
        coordinate.value,
        markerDisplayNames,
      ),
    )
    .join(" · ");
}

export function formatBucketLabel(
  bucketStart: string | null,
  bucket: MetricBucket,
): string {
  if (!bucketStart) {
    return "Total";
  }
  const date = new Date(bucketStart);
  if (bucket === "month") {
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      year: "numeric",
    }).format(date);
  }
  if (bucket === "hour") {
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
    }).format(date);
  }
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
  }).format(date);
}

export function formatUnits(value: number): string {
  if (!Number.isFinite(value)) {
    return "--";
  }
  return Math.round(value).toLocaleString();
}

export function formatPercent(value: number): string {
  if (!Number.isFinite(value)) {
    return "--";
  }
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

// --- envelope access ---------------------------------------------------

function findSeries(
  response: MetricsQueryResponse,
  key: string,
): MetricSeries | undefined {
  return (response.series ?? []).find((series) => series.key === key);
}

function seriesGroups(series: MetricSeries | undefined): MetricGroup[] {
  return series?.groups ?? [];
}

function groupPoints(group: MetricGroup | undefined): MetricPoint[] {
  return group?.points ?? [];
}

function sumPoints(points: MetricPoint[]): number {
  return points.reduce(
    (total, point) => total + (point.kind === "scalar" ? point.value : 0),
    0,
  );
}

function coordinateKey(group: MetricGroup): string {
  return (group.dimensions ?? [])
    .map((coordinate) => `${coordinate.dimension}=${coordinate.value}`)
    .join("&");
}

// --- geometry transforms ----------------------------------------------

// One-series-over-time line for a `group_by=[]` query (a single group whose
// points are the buckets). Points arrive pre-sorted from `core`.
export function bucketedLine(
  response: MetricsQueryResponse,
  key: string,
  bucket: MetricBucket,
): ChartDatum[] {
  const group = seriesGroups(findSeries(response, key))[0];
  return groupPoints(group).flatMap((point) =>
    point.kind !== "scalar"
      ? []
      : [
          {
            label: formatBucketLabel(point.bucket_start, bucket),
            value: point.value,
          },
        ],
  );
}

// Scalar total of a measure across every group + bucket it appears in.
export function seriesTotal(
  response: MetricsQueryResponse,
  key: string,
): number {
  return seriesGroups(findSeries(response, key)).reduce(
    (total, group) => total + sumPoints(groupPoints(group)),
    0,
  );
}

// Ranked category list from a `bucket=none, group_by=[dimension]` query: one
// datum
// per group, descending — the geometry the `top-list` / `bar` / `donut`
// primitives read.
export function categoryFromGroups(
  response: MetricsQueryResponse,
  key: string,
  markerDisplayNames?: MarkerDisplayNames,
): ChartDatum[] {
  return seriesGroups(findSeries(response, key))
    .map((group) => ({
      label: groupLabel(group, markerDisplayNames),
      value: sumPoints(groupPoints(group)),
    }))
    .sort((first, second) => second.value - first.value);
}

// Category list in the backend's group order (agent_version_id groups arrive
// ordered by UUIDv7 = creation order), preserved for the version regression
// axis where chronological order is the whole point — never re-sorted.
export function orderedCategoryFromGroups(
  response: MetricsQueryResponse,
  key: string,
  markerDisplayNames?: MarkerDisplayNames,
): ChartDatum[] {
  return seriesGroups(findSeries(response, key)).map((group) => ({
    label: groupLabel(group, markerDisplayNames),
    value: sumPoints(groupPoints(group)),
  }));
}

// `donut` reads the same {label,value} shape as a category list.
export function segmentsFromGroups(
  response: MetricsQueryResponse,
  key: string,
  markerDisplayNames?: MarkerDisplayNames,
): ChartSegment[] {
  return categoryFromGroups(response, key, markerDisplayNames);
}

// Horizontal stacked bar for a `bucket=none, group_by=[dimension]` query where
// each
// row is a group and the stack is the metric's measures (e.g. pass/warn/fail
// per agent_version — the regression axis).
export function stackedByGroup(
  response: MetricsQueryResponse,
  measures: readonly { key: string; label: string }[],
  markerDisplayNames?: MarkerDisplayNames,
): ChartStackedDatum[] {
  const rows = new Map<
    string,
    { label: string; values: Map<string, number> }
  >();
  const order: string[] = [];
  for (const measure of measures) {
    for (const group of seriesGroups(findSeries(response, measure.key))) {
      const key = coordinateKey(group);
      let row = rows.get(key);
      if (!row) {
        row = {
          label: groupLabel(group, markerDisplayNames),
          values: new Map(),
        };
        rows.set(key, row);
        order.push(key);
      }
      row.values.set(measure.key, sumPoints(groupPoints(group)));
    }
  }
  return order.map((key) => {
    const row = rows.get(key);
    return {
      label: row?.label ?? "",
      segments: measures.map((measure) => ({
        label: measure.label,
        value: row?.values.get(measure.key) ?? 0,
      })),
    };
  });
}

// Time-bucketed stacked bar: one row per bucket, one segment per group — the
// shape of a bucketed query with a group_by (e.g. usage over time split by
// event type). Buckets align across groups by `bucket_start`, not index.
export function stackedOverTime(
  response: MetricsQueryResponse,
  key: string,
  bucket: MetricBucket,
  markerDisplayNames?: MarkerDisplayNames,
): ChartStackedDatum[] {
  const groups = seriesGroups(findSeries(response, key));
  const bucketKeys = new Set<string>();
  for (const group of groups) {
    for (const point of groupPoints(group)) {
      bucketKeys.add(point.bucket_start ?? "total");
    }
  }
  const ordered = [...bucketKeys].sort();
  return ordered.map((bucketKey) => ({
    label: formatBucketLabel(bucketKey === "total" ? null : bucketKey, bucket),
    segments: groups.map((group) => ({
      label: groupLabel(group, markerDisplayNames),
      value: groupPoints(group)
        .filter((point) => (point.bucket_start ?? "total") === bucketKey)
        .reduce(
          (total, point) => total + (point.kind === "scalar" ? point.value : 0),
          0,
        ),
    })),
  }));
}

// Pass-rate (%) over time from a marker_pass_rate `group_by=[]` query, aligning
// the pass/warn/fail measures by bucket (not index) so a bucket missing one
// verdict still lands on the right point.
export function passRateOverTime(
  response: MetricsQueryResponse,
  bucket: MetricBucket,
): ChartDatum[] {
  const byBucket = new Map<
    string,
    { pass: number; warn: number; fail: number }
  >();
  const accumulate = (
    field: "pass" | "warn" | "fail",
    points: MetricPoint[],
  ) => {
    for (const point of points) {
      if (point.kind !== "scalar") {
        continue;
      }
      const key = point.bucket_start ?? "total";
      const entry = byBucket.get(key) ?? { pass: 0, warn: 0, fail: 0 };
      entry[field] += point.value;
      byBucket.set(key, entry);
    }
  };
  accumulate(
    "pass",
    groupPoints(seriesGroups(findSeries(response, "pass"))[0]),
  );
  accumulate(
    "warn",
    groupPoints(seriesGroups(findSeries(response, "warn"))[0]),
  );
  accumulate(
    "fail",
    groupPoints(seriesGroups(findSeries(response, "fail"))[0]),
  );
  return [...byBucket.keys()].sort().flatMap((key) => {
    const entry = byBucket.get(key);
    const pass = entry?.pass ?? 0;
    const total = pass + (entry?.warn ?? 0) + (entry?.fail ?? 0);
    return total === 0
      ? []
      : [
          {
            label: formatBucketLabel(key === "total" ? null : key, bucket),
            value: (pass / total) * 100,
          },
        ];
  });
}

// Aggregate pass-rate (%) over the whole window, or null when no verdicts fell
// in it (so the KPI shows "--" rather than a fake 0%).
export function passRateScalar(response: MetricsQueryResponse): number | null {
  const pass = seriesTotal(response, "pass");
  const total =
    pass + seriesTotal(response, "warn") + seriesTotal(response, "fail");
  return total === 0 ? null : (pass / total) * 100;
}

export function passRateByGroup(
  response: MetricsQueryResponse,
  preserveOrder = false,
  markerDisplayNames?: MarkerDisplayNames,
): ChartDatum[] {
  const rows = new Map<
    string,
    { label: string; pass: number; warn: number; fail: number }
  >();
  const order: string[] = [];
  const accumulate = (field: "pass" | "warn" | "fail") => {
    for (const group of seriesGroups(findSeries(response, field))) {
      const key = coordinateKey(group) || "all";
      let row = rows.get(key);
      if (!row) {
        row = {
          label: groupLabel(group, markerDisplayNames),
          pass: 0,
          warn: 0,
          fail: 0,
        };
        rows.set(key, row);
        order.push(key);
      }
      row[field] += sumPoints(groupPoints(group));
    }
  };
  accumulate("pass");
  accumulate("warn");
  accumulate("fail");
  const data = order.flatMap((key) => {
    const row = rows.get(key);
    const pass = row?.pass ?? 0;
    const total = pass + (row?.warn ?? 0) + (row?.fail ?? 0);
    return total === 0
      ? []
      : [
          {
            label: row?.label ?? "",
            value: (pass / total) * 100,
          },
        ];
  });
  return preserveOrder
    ? data
    : data.sort((first, second) => second.value - first.value);
}

export interface EligibilitySummary {
  eligible: number;
  notEligible: number;
  notEligibleRate: number | null;
}

export function eligibilitySummary(
  response: MetricsQueryResponse,
): EligibilitySummary | null {
  if (
    findSeries(response, "eligible") === undefined ||
    findSeries(response, "not_eligible") === undefined
  ) {
    return null;
  }
  const eligible = seriesTotal(response, "eligible");
  const notEligible = seriesTotal(response, "not_eligible");
  const total = eligible + notEligible;
  return {
    eligible,
    notEligible,
    notEligibleRate: total === 0 ? null : (notEligible / total) * 100,
  };
}

// A numerator/denominator rate (%) over the window (e.g. eval-failed rate),
// null when the denominator is empty.
export function ratioScalar(
  response: MetricsQueryResponse,
  numeratorKey: string,
  denominatorKey: string,
): number | null {
  const denominator = seriesTotal(response, denominatorKey);
  if (denominator === 0) {
    return null;
  }
  return (seriesTotal(response, numeratorKey) / denominator) * 100;
}

// A stable, human-neutral key for the query cache / dedup.
export function metricCacheKey(spec: {
  metric: MetricName;
  bucket: MetricBucket;
  group_by: readonly MetricDimension[];
  window: TimeWindow;
  agent_id?: string;
  filters?: Record<string, unknown>;
}): Record<string, string> {
  const filters = Object.entries(spec.filters ?? {})
    .filter(([, value]) => value !== null && value !== undefined)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => `${key}=${String(value)}`)
    .join("&");
  return {
    metric: spec.metric,
    bucket: spec.bucket,
    group_by: spec.group_by.join(","),
    start: spec.window.start,
    end: spec.window.end,
    agent_id: spec.agent_id ?? "",
    filters,
  };
}
